import React, { useRef, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import './HireMeSection.css';
import { EMAILJS_CONFIG } from '../../config/emailjs.config';

const HireMeSection = () => {
  const form = useRef();
  const textareaRef = useRef(null);
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    fullstackApp: false,
    frontendDesign: false,
    aiTool: false,
    ecommerce: false,
    educationalPlatform: false,
    portfolioWebsite: false
  });

  // Auto-resize textarea as user types
  useEffect(() => {
    const adjustTextareaHeight = () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = '30px';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    };
    
    adjustTextareaHeight();
  }, [formData.message]);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [id.replace('opt-', '')]: checked });
    } else {
      setFormData({ ...formData, [id]: value });
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name.trim() || !formData.email.trim()) {
      setFormStatus({
        submitting: false,
        submitted: false,
        error: 'Please fill in all required fields'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({
        submitting: false,
        submitted: false,
        error: 'Please enter a valid email address'
      });
      return;
    }

    setFormStatus({ submitting: true, submitted: false, error: null });

    try {
      // Get selected services
      const selectedServices = [];
      if (formData['1']) selectedServices.push('Fullstack App');
      if (formData['2']) selectedServices.push('Frontend Design');
      if (formData['4']) selectedServices.push('AI Tool');
      if (formData['6']) selectedServices.push('E-commerce Platform');
      if (formData['7']) selectedServices.push('Educational Platform');
      if (formData['8']) selectedServices.push('Portfolio Website');
      
      const servicesString = selectedServices.join(', ') || 'General Inquiry';

      // Send email to you (the portfolio owner)
      await emailjs.sendForm(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID_OWNER,
        form.current,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      // Send thank you email to the user
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID_USER,
        {
          to_name: formData.name,
          to_email: formData.email,
          from_name: 'Mena Emad',
          message: 'Thank you for contacting me! I will get back to you as soon as possible.',
          services: servicesString,
          user_name: formData.name,
          user_email: formData.email,
          user_message: formData.message,
          service: servicesString
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      setFormStatus({
        submitting: false,
        submitted: true,
        error: null
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        message: '',
        fullstackApp: false,
        frontendDesign: false,
        aiTool: false,
        ecommerce: false,
        educationalPlatform: false,
        portfolioWebsite: false
      });
      
      // Reset checkboxes
      document.getElementById('opt-1').checked = false;
      document.getElementById('opt-2').checked = false;
      document.getElementById('opt-4').checked = false;
      document.getElementById('opt-6').checked = false;
      document.getElementById('opt-7').checked = false;
      document.getElementById('opt-8').checked = false;
      
    } catch (error) {
      console.error('Email sending failed:', error);
      setFormStatus({
        submitting: false,
        submitted: false,
        error: 'Failed to send message. Please try again later.'
      });
    }
  };

  return (
    <div className="hire">
      <h2>What Do You Want Me To Do?</h2>
      {formStatus.submitted ? (
        <div className="success-message">
          <h3>Thank you for your message!</h3>
          <p>I'll get back to you as soon as possible.</p>
        </div>
      ) : (
        <form className="work-request" ref={form} onSubmit={sendEmail}>
          <div className="work-request--options">
            <div className="options-container">
              <span className="options-a">
                <div className="option-item">
                  <input 
                    id="opt-1" 
                    type="checkbox" 
                    name="service" 
                    value="Fullstack App" 
                    onChange={handleInputChange}
                  />
                  <label htmlFor="opt-1">
                    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                      viewBox="0 0 150 111" style={{ enableBackground: 'new 0 0 150 111' }} xmlSpace="preserve">
                      <g transform="translate(0.000000,111.000000) scale(0.100000,-0.100000)">
                        <path d="M950,705L555,310L360,505C253,612,160,700,155,700c-6,0-44-34-85-75l-75-75l278-278L550-5l475,475c261,261,475,480,475,485c0,13-132,145-145,145C1349,1100,1167,922,950,705z" />
                      </g>
                    </svg>
                    Fullstack App
                  </label>
                </div>
                <div className="option-item">
                  <input 
                    id="opt-2" 
                    type="checkbox" 
                    name="service" 
                    value="Frontend Design" 
                    onChange={handleInputChange}
                  />
                  <label htmlFor="opt-2">
                    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                      viewBox="0 0 150 111" style={{ enableBackground: 'new 0 0 150 111' }} xmlSpace="preserve">
                      <g transform="translate(0.000000,111.000000) scale(0.100000,-0.100000)">
                        <path d="M950,705L555,310L360,505C253,612,160,700,155,700c-6,0-44-34-85-75l-75-75l278-278L550-5l475,475c261,261,475,480,475,485c0,13-132,145-145,145C1349,1100,1167,922,950,705z" />
                      </g>
                    </svg>
                    Frontend Design
                  </label>
                </div>
                <div className="option-item">
                  <input 
                    id="opt-4" 
                    type="checkbox" 
                    name="service" 
                    value="AI Tool" 
                    onChange={handleInputChange}
                  />
                  <label htmlFor="opt-4">
                    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                      viewBox="0 0 150 111" style={{ enableBackground: 'new 0 0 150 111' }} xmlSpace="preserve">
                      <g transform="translate(0.000000,111.000000) scale(0.100000,-0.100000)">
                        <path d="M950,705L555,310L360,505C253,612,160,700,155,700c-6,0-44-34-85-75l-75-75l278-278L550-5l475,475c261,261,475,480,475,485c0,13-132,145-145,145C1349,1100,1167,922,950,705z" />
                      </g>
                    </svg>
                    AI Tool
                  </label>
                </div>
              </span>
              <span className="options-b">
                <div className="option-item">
                  <input 
                    id="opt-6" 
                    type="checkbox" 
                    name="service" 
                    value="E-commerce Platform" 
                    onChange={handleInputChange}
                  />
                  <label htmlFor="opt-6">
                    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                      viewBox="0 0 150 111" style={{ enableBackground: 'new 0 0 150 111' }} xmlSpace="preserve">
                      <g transform="translate(0.000000,111.000000) scale(0.100000,-0.100000)">
                        <path d="M950,705L555,310L360,505C253,612,160,700,155,700c-6,0-44-34-85-75l-75-75l278-278L550-5l475,475c261,261,475,480,475,485c0,13-132,145-145,145C1349,1100,1167,922,950,705z" />
                      </g>
                    </svg>
                    E-commerce Platform
                  </label>
                </div>
                <div className="option-item">
                  <input 
                    id="opt-7" 
                    type="checkbox" 
                    name="service" 
                    value="Educational Platform" 
                    onChange={handleInputChange}
                  />
                  <label htmlFor="opt-7">
                    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                      viewBox="0 0 150 111" style={{ enableBackground: 'new 0 0 150 111' }} xmlSpace="preserve">
                      <g transform="translate(0.000000,111.000000) scale(0.100000,-0.100000)">
                        <path d="M950,705L555,310L360,505C253,612,160,700,155,700c-6,0-44-34-85-75l-75-75l278-278L550-5l475,475c261,261,475,480,475,485c0,13-132,145-145,145C1349,1100,1167,922,950,705z" />
                      </g>
                    </svg>
                    Educational Platform
                  </label>
                </div>
                <div className="option-item">
                  <input 
                    id="opt-8" 
                    type="checkbox" 
                    name="service" 
                    value="Portfolio Website" 
                    onChange={handleInputChange}
                  />
                  <label htmlFor="opt-8">
                    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                      viewBox="0 0 150 111" style={{ enableBackground: 'new 0 0 150 111' }} xmlSpace="preserve">
                      <g transform="translate(0.000000,111.000000) scale(0.100000,-0.100000)">
                        <path d="M950,705L555,310L360,505C253,612,160,700,155,700c-6,0-44-34-85-75l-75-75l278-278L550-5l475,475c261,261,475,480,475,485c0,13-132,145-145,145C1349,1100,1167,922,950,705z" />
                      </g>
                    </svg>
                    Portfolio Website
                  </label>
                </div>
              </span>
            </div>
          </div>
          
          <div className="work-request--information">
            <div className="information-name">
              <input 
                id="name" 
                type="text" 
                name="user_name" 
                spellCheck="false" 
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder=" "
              />
              <label htmlFor="name">Name</label>
            </div>
            <div className="information-email">
              <input 
                id="email" 
                type="email" 
                name="user_email" 
                spellCheck="false" 
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder=" "
              />
              <label htmlFor="email">Email</label>
            </div>
            
          </div>
          
          <div className="information-message">
            <textarea 
              id="message" 
              name="user_message" 
              spellCheck="false" 
              value={formData.message}
              onChange={handleInputChange}
              required
              placeholder=" "
              ref={textareaRef}
            ></textarea>
            <label htmlFor="message">Message</label>
          </div>
          
          {formStatus.error && (
            <div className="error-message">
              <p>{formStatus.error}</p>
            </div>
          )}
          <button 
            type="submit" 
            disabled={formStatus.submitting}
            className={formStatus.submitting ? 'submitting' : ''}
          >
            {formStatus.submitting ? 'Sending...' : 'Send Request'}
          </button>
        </form>
      )}
    </div>
  );
};

export default HireMeSection; 